/**
 * AdHocBuyerPreview — podgląd danych nabywcy JEDNOKROTNEGO na formatce dokumentu.
 *
 * Układ odwzorowuje Symfonię: w polu „Nazwa" stoi nazwa nabywcy, a obok przycisk otwierający
 * okno z pełnymi danymi. Powód jest ten sam co tam — takiego nabywcy nie ma w kartotece, więc
 * nie da się „wejść w kontrahenta", żeby zobaczyć adres; dane żyją wyłącznie na dokumencie.
 *
 * Okno jest TYLKO DO ODCZYTU, jak cała formatka: dokumentu wystawionego w Symfonii nie edytujemy
 * przez WebAPI.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HmDocumentAddress } from '@saas/shared';
import { Contact } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';

import { Field } from './detail-parts';

export function AdHocBuyerPreview({
  name,
  address,
}: {
  name: string | null;
  address: HmDocumentAddress | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="grid grid-cols-[110px_1fr] items-center gap-2">
        <span className="text-sm text-text-secondary">{t('tenant.handelMagazyn.detail.name')}</span>
        <div className="flex items-center gap-1">
          <div className="min-w-0 flex-1 rounded-md border border-border-primary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-primary">
            <span className="truncate">{name?.trim() || '—'}</span>
            <span className="ml-2 rounded bg-bg-secondary px-1.5 py-0.5 text-[11px] text-text-secondary">
              {t('tenant.handelMagazyn.adHoc.badge')}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(true)}
            title={t('tenant.handelMagazyn.adHoc.previewTitle')}
            aria-label={t('tenant.handelMagazyn.adHoc.previewTitle')}
            className="h-9 w-9 shrink-0 p-0"
            data-testid="hm-adhoc-preview-open"
          >
            <Contact className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t('tenant.handelMagazyn.adHoc.previewTitle')}
        description={t('tenant.handelMagazyn.adHoc.previewSubtitle')}
        size="md"
      >
        <div className="space-y-2">
          <Field label={t('tenant.handelMagazyn.detail.taxId')} value={address?.taxId} />
          <Field label={t('tenant.handelMagazyn.detail.name')} value={name} />
          <Field label={t('tenant.handelMagazyn.adHoc.street')} value={address?.street} />
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('tenant.handelMagazyn.adHoc.houseNo')} value={address?.houseNo} />
            <Field
              label={t('tenant.handelMagazyn.adHoc.apartmentNo')}
              value={address?.apartmentNo}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('tenant.handelMagazyn.adHoc.postCode')} value={address?.postCode} />
            <Field label={t('tenant.handelMagazyn.adHoc.city')} value={address?.city} />
          </div>
        </div>
      </Modal>
    </>
  );
}
